/*
  Turning one report into the shapes the screen, the csv and the pdf each need.
  Kept out of the components so the pdf never has to reach into the dom to find
  out what is being shown.
*/

import { parseDay } from './period';

const monthLabels = [
  "jan", "feb", "mrt", "apr", "mei", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

const monthKey = (day) => day.slice(0, 7);

const monthLabel = (key) => {
  const [year, month] = key.split("-");

  return `${monthLabels[Number(month) - 1]} ${String(year).slice(2)}`;
};

const dayLabel = (day) => {
  const date = parseDay(day);

  return `${date.getDate()}/${date.getMonth() + 1}`;
};

/**
 * The daily rows are what the queries produce; a chart of three hundred of them
 * is a smear. Anything longer than a couple of months is rolled up per month,
 * which is also the grain a board reads a year in.
 */
export const buildSeries = (daily = [], threshold = 62) => {
  if (daily.length <= threshold) {
    return {
      grain: "day",
      points: daily.map((row) => ({
        key: row.day,
        label: dayLabel(row.day),
        toppedUp: row.toppedUp,
        spent: row.spent,
        transactions: row.transactions,
      })),
    };
  }

  const byMonth = new Map();

  for (const row of daily) {
    const key = monthKey(row.day);
    const point = byMonth.get(key) ?? {
      key,
      label: monthLabel(key),
      toppedUp: 0,
      spent: 0,
      transactions: 0,
    };

    point.toppedUp += row.toppedUp;
    point.spent += row.spent;
    point.transactions += row.transactions;
    byMonth.set(key, point);
  }

  return {
    grain: "month",
    points: [...byMonth.values()].map((point) => ({
      ...point,
      toppedUp: Math.round(point.toppedUp * 100) / 100,
      spent: Math.round(point.spent * 100) / 100,
    })),
  };
};

/**
 * The cash lines in the order a board reads them, with the sign each one moves
 * the wallet in. One list so the tile row, the csv and the pdf cannot drift
 * apart on wording.
 */
export const cashflowLines = (report) => [
  {
    key: "toppedUp",
    label: "Opgeladen",
    value: report.cashflow.toppedUp,
    hint: "Geld dat leden op hun kaart zetten",
    tone: "in",
  },
  {
    key: "spent",
    label: "Verbruikt",
    value: report.cashflow.spent,
    hint: "Van kaarten afgeschreven aan de toog",
    tone: "out",
  },
  {
    key: "net",
    label: "Netto kasstroom",
    value: report.cashflow.net,
    hint: "Opgeladen min verbruikt",
    tone: report.cashflow.net >= 0 ? "in" : "out",
  },
  {
    key: "purchases",
    label: "Inkoop",
    value: report.cashflow.purchases,
    hint: "Goedgekeurde bonnen in deze periode",
    tone: "out",
  },
  {
    key: "result",
    label: "Resultaat",
    value: report.cashflow.result,
    hint: "Verbruikt min inkoop",
    tone: report.cashflow.result >= 0 ? "in" : "out",
  },
  {
    key: "outstanding",
    label: "Openstaand saldo",
    value: report.balance.outstanding,
    hint: "Wat vandaag op de kaarten staat",
    tone: "neutral",
  },
];
